import { useCallback, useState } from 'react';
import {
  useAuth as useFronteggAuth,
  useLoginWithRedirect,
} from '@frontegg/react';
import { logger } from 'src/services';
import { isFromCareConnect } from 'src/utils';
import { useAuthStore } from './useAuthStore';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { useChatValidationStore } from '../chat';
import {
  getAppointmentConditionsApi,
  updateAppointmentDetailsApi,
  GetAppointmentConditionsApiPayload,
  UpdateAppointmentDetailsApiPayload,
} from 'src/api';

export const useAuth = () => {
  const {
    user,
    isAuthenticated: isPractitionerAuthenticated,
    isLoading: isFronteggLoading,
  } = useFronteggAuth();
  const fronteggLoginWithRedirect = useLoginWithRedirect();
  const isPractitioner = isFromCareConnect();
  const [patientAuthModal, setPatientAuthModal] = useState(false);

  const {
    token,
    loading,
    error,
    appointment,
    showConditionSelector,
    validateLoggedInUser,
    validateAnonymousUser,
  } = useAuthStore();

  const isAuthenticated = isPractitioner
    ? isPractitionerAuthenticated
    : !!token;

  const authenticate = useCallback(() => {
    if (isPractitioner) {
      fronteggLoginWithRedirect();
    } else {
      useAuthStore.setState({ error: null, loading: false });
      setPatientAuthModal(true);
    }
  }, [fronteggLoginWithRedirect]);

  const getAppointmentConditions = useCallback(
    async ({ appointmentId }: GetAppointmentConditionsApiPayload) => {
      try {
        const response = await getAppointmentConditionsApi({ appointmentId });
        return response.data.condition_details;
      } catch (error) {
        toast.error('Something went wrong!');
        logger.error(
          'Failed to get generic conditions',
          { appointmentId },
          error as Error
        );
      }
    },
    []
  );

  const updateAppointment = useCallback(
    async ({
      appointmentId,
      conditionId,
    }: UpdateAppointmentDetailsApiPayload) => {
      try {
        const { data } = await updateAppointmentDetailsApi({
          appointmentId,
          conditionId,
        });
        useAuthStore.setState({ showConditionSelector: false });
        validateAnonymousUser({
          chatToken: useChatValidationStore.getState().token!,
          dateOfBirth: data.date_of_birth,
          skipGenericCheck: true,
        });
      } catch (error: any) {
        const message = 'Failed to update appointment';
        toast.error(message);
        logger.error(message, { appointment }, error);
      }
    },
    []
  );

  return {
    user,
    isAuthenticated,
    isLoading: loading || isFronteggLoading,
    isPractitioner,
    authenticate,
    patientAuthModal,
    validateAnonymousUser,
    validateLoggedInUser,
    error,
    appointment,
    showConditionSelector,
    getAppointmentConditions,
    updateAppointment,
  };
};
